/**
 * A device-local memory of the legal acknowledgement.
 *
 * WHAT THIS FIXES. `legalAcknowledged` started life as `useState(false)` and was
 * only ever set from the server, inside the `/api/settings/all` hydrate. So on
 * every COLD START — which is what iOS gives you after the app has been in the
 * background a while, because it evicts the process — the splash was shown until
 * a network round-trip came back, and if that request failed it was shown
 * forever. There was never a timeout to tune: the app simply had no memory of
 * having been told, and re-asked from scratch each time it was launched fresh.
 *
 * `isConsentCurrent` already had the right instinct — "server hash unknown, trust
 * the existing ack" — but it was never reached when the fetch threw, because the
 * initial state was the un-acknowledged one. This gives the boot an answer to
 * start from.
 *
 * The server stays authoritative. This is a cache, not a second source of truth:
 * the hydrate still re-checks the stored hash against `/api/legal/current`, so a
 * change to the legal text still re-prompts exactly as it did before. All this
 * removes is the gap where the app knew nothing.
 *
 * Keyed by account so a device two trainers share cannot have one of them
 * inherit the other's acceptance.
 */

const KEY = "nexus.legalAck.v1";

export interface LocalLegalAck {
  accountId: number | null;
  acknowledgedAt: number;
  /** The legal version hash accepted, when it was known at the time. */
  acknowledgedHash: string | null;
}

export function readLocalLegalAck(accountId: number | null): LocalLegalAck | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LocalLegalAck>;
    if (typeof parsed?.acknowledgedAt !== "number") return null;
    const stored = typeof parsed.accountId === "number" ? parsed.accountId : null;
    // A record belonging to somebody else is not ours to honour. A record with
    // no account (accepted before sign-in) is still this device's own.
    if (stored != null && accountId != null && stored !== accountId) return null;
    return {
      accountId: stored,
      acknowledgedAt: parsed.acknowledgedAt,
      acknowledgedHash:
        typeof parsed.acknowledgedHash === "string" ? parsed.acknowledgedHash : null,
    };
  } catch {
    return null;
  }
}

export function writeLocalLegalAck(
  accountId: number | null,
  acknowledgedHash: string | null,
): void {
  try {
    if (typeof window === "undefined") return;
    const record: LocalLegalAck = {
      accountId,
      acknowledgedAt: Date.now(),
      acknowledgedHash,
    };
    window.localStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    /* a cache we cannot write costs one extra prompt, never a wrong one. */
  }
}

export function clearLocalLegalAck(): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(KEY);
  } catch {
    /* nothing to do. */
  }
}
